import React from 'react';
import { Box, Card, CardContent, Skeleton } from '@mui/material';
import { Typography } from '@mui/joy';
import CheckCircleRounded from '@mui-symbols-material/w200/CheckCircleRounded';
import { IoTIcon } from '@dreebit-registry/vsm-microservice-components';
import { useTranslation } from 'react-i18next';
import { connectionStatusIconFunc, connectionStatusTextFunc } from '../IoTConnectionWidget/connectionStatus';

interface Props {
    connectionStatus?: string;
    signalStrength?: number;
    networkLoading?: boolean;
}

const AssetIoTConnection = ({ connectionStatus, signalStrength, networkLoading }: Props) => {
    const { t } = useTranslation();

    const getNetworkType = (strength?: number): string => {
        if (!strength) return 'Unknown';
        if (strength >= 80) return '4G';
        if (strength >= 60) return '3G';
        return '2G';
    };

    return (
        <Card sx={{
            border: "1px solid #cccccc",
            borderRadius: "4px",
            height: "100%",
            boxShadow: "none"
        }}>
            <CardContent sx={{ padding: "20px", height: "100%" }}>
                {/* Header */}
                <Box sx={{ display: "flex", gap: "8px", alignItems: "center", marginBottom: "8px" }}>
                    <IoTIcon width={28} height={28} color={'#333333'} />
                    <Typography level="titleMedium" sx={{ fontSize: "18px", lineHeight: "25px", color: "#333" }}>
                        Connection
                    </Typography>
                </Box>

                {/* Alerts Section */}
                <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 16px",
                    borderRadius: "3px",
                    marginBottom: "8px"
                }}>
                    <Box>
                        <Typography level="titleSmall" sx={{ fontSize: "16px", lineHeight: "22px", color: "#333" }}>
                            Alerts
                        </Typography>
                        <Typography level="titleLarge" sx={{ fontSize: "24px", lineHeight: "33px", color: "#333" }}>
                            No alerts
                        </Typography>
                        <Typography sx={{ fontSize: "12px", lineHeight: "15px", fontWeight: 700, color: "#333" }}>
                            0 errors & 0 warnings
                        </Typography>
                    </Box>
                </Box>

                {/* Connection Status & Network */}
                <Box sx={{
                    display: "flex",
                    borderTop: "1px solid #cccccc",
                    height: "260px"
                }}>
                    {/* Connection Status */}
                    <Box sx={{
                        flex: 1,
                        borderRight: "1px solid #cccccc",
                        padding: "16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                        alignItems: "center"
                    }}>
                        <Typography level="titleSmall" sx={{ fontSize: "16px", lineHeight: "22px", color: "#333", width: "100%" }}>
                            Connection status
                        </Typography>
                        {connectionStatusIconFunc({ connectionStatus: connectionStatus ?? '', size: 100 })}
                        <Typography sx={{ fontSize: "42px", lineHeight: "48px", fontWeight: 400, color: "#333", textAlign: "center" }}>
                            {connectionStatusTextFunc({ connectionStatus: connectionStatus ?? '', t }) || t('Unknown')}
                        </Typography>
                    </Box>

                    {/* Network */}
                    <Box sx={{
                        flex: 1,
                        padding: "16px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "20px",
                        alignItems: "center"
                    }}>
                        <Typography level="titleSmall" sx={{ fontSize: "16px", lineHeight: "22px", color: "#333", width: "100%" }}>
                            Network
                        </Typography>
                        {networkLoading ? (
                            <>
                                <Skeleton variant="circular" width={100} height={100} />
                                <Skeleton variant="text" width={80} height={48} />
                            </>
                        ) : (
                            <>
                                <CheckCircleRounded sx={{ fontSize: 100, color: "#82c18b" }} />
                                <Typography sx={{ fontSize: "42px", lineHeight: "48px", fontWeight: 400, color: "#333", textAlign: "center" }}>
                                    {getNetworkType(signalStrength)}
                                </Typography>
                            </>
                        )}
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default AssetIoTConnection;
